import { apiRequest } from "./api-request";
import { ModelConfigError } from "./model-config";

// The authored company graph and what questions did with it — the data behind `/admin/company-graph`.
//
// Read-only and read ONCE per page load for the graph itself: it is a fixture baked into the
// agent-service image, so it cannot change while the page is open. The runs are re-read, because
// every question on the graph route adds one.

/**
 * "whichever graph the server calls the default" — what the bare slash command sends.
 *
 * Not a graph id: resolving one here would mean the SPA deciding which graph is the default, and
 * the catalogue that actually holds them is the only thing that can know.
 */
export const DEFAULT_GRAPH = "*";

/** One graph a reader can pick, as the menu needs it — never enough to answer from. */
export interface GraphRef {
  id: string;
  name: string;
  companies: number;
  nodes: number;
  edges: number;
  /** Where it came from — the build that made it. */
  source: string;
  /** THE ONE EVERY QUESTION GOES TO. Exactly one graph carries it, and an admin decides which. */
  active: boolean;
}

/**
 * Makes this graph the one every question is answered from — for everybody, not for the caller.
 *
 * There is only ever one, so this is a swap and not a toggle: the graph that held it stops being
 * active in the same breath. Admin-only, enforced where it is done rather than by hiding this.
 */
export async function activateCompanyGraph(id: string): Promise<void> {
  const { response } = await apiRequest(`ai/company-graph/${encodeURIComponent(id)}/active`, {
    method: "PUT",
    csrf: true,
  });
  if (!response.ok) throw new ModelConfigError(response.status, `Failed to activate the graph (${response.status})`);
}

/**
 * Removes a graph. There is no undo and no copy kept.
 *
 * Any graph may go: one used to ship inside the service and be refused here with a 409, and that
 * rule lived where the deleting happens rather than in this page — which is why nothing here had
 * to change when it went away.
 */
export async function deleteCompanyGraph(id: string): Promise<void> {
  const { response } = await apiRequest(`ai/company-graph/${encodeURIComponent(id)}`, {
    method: "DELETE",
    // Mutating, so the BFF demands the token — and it is opt-in here, not inferred from the
    // method, so leaving it off is a 403 the caller never sees coming.
    csrf: true,
  });
  if (!response.ok) throw new ModelConfigError(response.status, `Failed to delete the graph (${response.status})`);
}

export async function loadCompanyGraphs(): Promise<GraphRef[]> {
  const { response } = await apiRequest("ai/company-graphs", { method: "GET" });
  if (!response.ok) throw new ModelConfigError(response.status, `Failed to load the graphs (${response.status})`);
  return (await response.json()) as GraphRef[];
}

/** A thing companies relate to — `Blutprodukte`, `Klinik`. The other half of the bipartite drawing. */
export interface GraphNode {
  label: string;
  aliases?: string[];
  /**
   * Which input named this thing: `summary`, `label` or `record` — the most PUBLIC one, where
   * several did.
   *
   * Absent on a graph built before the sources existed, which is a graph read out of summaries and
   * nothing else; every reader of this field defaults it to `summary` for that reason. A reader
   * without `read:organizational-record` is served a document with the `record` nodes already
   * taken out, so their absence here is not evidence that the graph was built without the Akte.
   */
  source?: string;
}

/** A group of nodes that are kinds of one thing. Drawn, never searched — see the Kotlin model. */
export interface GraphCluster {
  label: string;
  members: string[];
  /**
   * Which layer this group sits in: 1 groups nodes, 2 groups the labels of level 1, and so on.
   *
   * Optional because a graph grouped before levels existed has none, and that graph is a graph
   * with one level — which is what reading a missing one as 1 says.
   */
  level?: number;
}

export interface GraphEdge {
  company: string;
  predicate: string;
  object: string;
  /**
   * How many member edges this one stands for; 1 for an ordinary edge.
   *
   * Only ever set by the collapse: the server has no such field, because a company's tie to a
   * group is a fact about its edges to the members and storing it would be a second source for it.
   */
  weight?: number;
  status?: string | null;
  /**
     Only on SUPPLIES, and always a company: supply is ternary — who supplies WHAT to WHOM — so the
     customer is not the object and gets an arrow of its own in the drawing.
   */
  to?: string | null;
  /** Why the author put this edge in. Shown on the edge, never used to decide anything. */
  evidence?: string;
}

export interface GraphPredicate {
  name: string;
  de?: string;
  en?: string;
  de_says?: string;
  en_says?: string;
}

export interface CompanyGraph {
  /** Its own name for itself — what a run is recorded against and what the menu lists. */
  id?: string;
  name?: string;
  /** The coarse layer, absent on a graph that has not been grouped. */
  clusters?: GraphCluster[];
  companies: string[];
  nodes: GraphNode[];
  predicates: GraphPredicate[];
  edges: GraphEdge[];
}

/** One demand of a question, after resolution — what the words actually became. */
export interface GraphRunDemand {
  thing: string;
  /** False when the graph holds no node for the word: the run highlights nothing, and says so. */
  resolved: boolean;
  relation?: string | null;
}

export interface GraphRunEdge {
  company: string;
  predicate: string;
  obj: string;
  status?: string | null;
}

export interface CompanyGraphRun {
  at: string;
  question: string;
  /** The graph this walk was against — a run on another one highlights nothing here. */
  graphId?: string | null;
  language?: string | null;
  demands: GraphRunDemand[];
  edges: GraphRunEdge[];
  companies: string[];
}

export async function loadCompanyGraph(id?: string | null): Promise<CompanyGraph> {
  const where = id && id !== DEFAULT_GRAPH ? `?id=${encodeURIComponent(id)}` : "";
  const { response } = await apiRequest(`ai/company-graph${where}`, { method: "GET" });
  if (!response.ok) throw new ModelConfigError(response.status, `Failed to load the company graph (${response.status})`);
  return (await response.json()) as CompanyGraph;
}

/**
 * The caller's own walks, newest first, across every conversation they have had.
 *
 * There is no id to pass: the server keys them by the token's subject, so this can only ever
 * return the caller's. An empty list is the answer before they have taken the graph route at all.
 */
export async function loadCompanyGraphRuns(): Promise<CompanyGraphRun[]> {
  const { response } = await apiRequest("ai/company-graph/runs", { method: "GET" });
  if (!response.ok) throw new ModelConfigError(response.status, `Failed to load the graph runs (${response.status})`);
  return (await response.json()) as CompanyGraphRun[];
}

/** A company a graph could be built from. */
export interface CompanyRef {
  id: string;
  name: string;
}

/**
 * Every company the builder can build from.
 *
 * Read from agent-service rather than from the SPA's own company search, so the picker and the
 * build cannot disagree about what the corpus holds — a picker fed from somewhere else can offer a
 * company the build then reports as "not in the corpus".
 */
export async function loadBuildableCompanies(): Promise<CompanyRef[]> {
  const { response } = await apiRequest("ai/companies", { method: "GET" });
  if (!response.ok) throw new ModelConfigError(response.status, `The companies could not be read (${response.status})`);
  return (await response.json()) as CompanyRef[];
}

/** One build, as a reader watching it sees it. `total` null means the stage has nothing to count. */
export interface BuildJob {
  jobId: string;
  graphId: string;
  /** The graph this one grew from, or absent when it was built from nothing. */
  base?: string | null;
  companies: number;
  startedAt: string;
  /** `running`, then exactly one of `done` or `failed`. A poll stops at either. */
  status: "running" | "done" | "failed";
  stage: string;
  done: number;
  total?: number | null;
  counts?: BuildCounts | null;
  /** Where it was written, or null when it lives only in that process until a restart. */
  file?: string | null;
  warning?: string | null;
  error?: string | null;
}

/** What each stage took in and let through — the readable half of a disappointing graph. */
export interface BuildCounts {
  asked: number;
  fetched: number;
  notFound: string[];
  unreadable: number;
  proposed: number;
  unfaithful: number;
  spellings: number;
  unembeddable: number;
  nodes: number;
  edges: number;
  danglingCustomers: number;
  /** Words the model reused from the graph on its own. */
  reused: number;
  /** Words our cosine folded onto an existing node — drift the model introduced and we caught. */
  rescued: number;
  /** Words that became new nodes. */
  coined: number;
  /**
   * New nodes sitting close to one the graph already had — drift that got through.
   *
   * Each one splits a node in two: every later question about it gets half an answer and nothing
   * errors. One of these is worse than five honest new nodes.
   */
  suspicious: string[];
  /**
   * Companies the build asked about that ended with no edges, each with its reason.
   *
   * The one thing the finished graph cannot tell you: an empty company looks the same whether it
   * was never asked, would not answer, or genuinely had nothing of these kinds to say.
   */
  silent?: string[];
  /**
   * Things the model named that are companies of this graph — dropped.
   *
   * Not a defect in the summary: "X betreibt das Y gGmbH" really is a relation to Y. It is a limit
   * of what the graph can hold, where a company and a thing share one namespace.
   */
  namedACompany?: string[];
  /** Texts asked about, which is one per summary, Akte entry, filed document and discussion. */
  texts?: number;
  /** Whether the Organisationsakte could be read at all: ALLOWED, DENIED or UNDECIDED. */
  recordAccess?: string;
  /** Entries, filed documents and discussion threads read out of the Organisationsakten. */
  recordItems?: number;
  /** Filed documents whose text could not be extracted, so that document contributed nothing. */
  recordUnreadable?: string[];
  /** Expertise labels read, and those that were typed but reached no node of their own. */
  labelsRead?: number;
  labelsOverlapping?: number;
  labelsCoined?: number;
  /** `RELATES` edges a label produced — the ones no summary and no record had already said. */
  labelEdges?: number;
}

export interface BuildInput {
  id: string;
  name: string;
  companies: string[];
  replace?: boolean;
  clusterThreshold?: number;
  /** An existing graph to add these companies to; absent builds from nothing. */
  base?: string;
  /** Whether the extraction prompt is shown the words the graph already has. */
  showVocabulary?: boolean;
}

/** A build that names an existing graph without `replace` comes back as this, not as an error. */
export class GraphExistsError extends Error {}

export async function startGraphBuild(input: BuildInput): Promise<BuildJob> {
  const { response } = await apiRequest("ai/company-graph/build", {
    method: "POST",
    csrf: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (response.status === 409) throw new GraphExistsError(input.id);
  if (!response.ok) throw new ModelConfigError(response.status, `The build could not be started (${response.status})`);
  return (await response.json()) as BuildJob;
}

/**
 * Starts grouping a graph's nodes into kinds. Polled with `loadGraphBuild`, like a build.
 *
 * Its own call and not part of a build: grouping re-runs without re-extracting, and it works on a
 * graph that already exists.
 */
export async function startClustering(id: string): Promise<BuildJob> {
  const { response } = await apiRequest(`ai/company-graph/${encodeURIComponent(id)}/cluster`, {
    method: "POST",
    csrf: true,
  });
  if (!response.ok) throw new ModelConfigError(response.status, `The grouping could not be started (${response.status})`);
  return (await response.json()) as BuildJob;
}

export async function loadGraphBuild(jobId: string): Promise<BuildJob> {
  const { response } = await apiRequest(`ai/company-graph/build/${encodeURIComponent(jobId)}`, { method: "GET" });
  if (!response.ok) throw new ModelConfigError(response.status, `The build could not be read (${response.status})`);
  return (await response.json()) as BuildJob;
}

/** A stable key for an edge, so a run's edges can be looked up while drawing without a nested scan. */
export const edgeKey = (company: string, predicate: string, object: string): string =>
  `${company}\u0000${predicate}\u0000${object}`;
